/**
 * The client store: a small zustand store fed only by server messages (CLAUDE.md rule 2). It
 * keeps the latest view exactly as received, plus a few UI facts the view cannot carry: the
 * handshake QR token, the replay, and the "recorded" moment of the last handshake.
 */
import type {
  HandshakeTokenMessage,
  PublicView,
  ReplayData,
  RoomMessage,
  ServerMessage,
  WelcomeMessage,
} from "@handshake/protocol";
import { create } from "zustand";
import {
  GameConnection,
  type Ack,
  type ConnectionStatus,
  type Identity,
  type SocketFactory,
} from "./connection.ts";
import type { CardId, Outgoing, PlayerView, Session } from "./types.ts";

export interface Toast {
  readonly id: number;
  readonly text: string;
  readonly tone: "info" | "error";
}

/**
 * The server's clock as last measured: `serverNow` when a view arrived at local `receivedAt`,
 * running `scale` times faster than real time (dev tools' DEV_TIME_SCALE; 1 in production).
 */
export interface ServerClock {
  readonly serverNow: number;
  readonly receivedAt: number;
  readonly scale: number;
}

export interface GameState {
  session: Session | null;
  status: ConnectionStatus | "idle";
  statusDetail: string | null;
  welcome: WelcomeMessage | null;
  /** A player's view. */
  view: PlayerView | null;
  /** A big screen's view (§15.8): the public view only. */
  publicView: PublicView | null;
  /** A big screen waiting to be paired: the code it shows. */
  pairingCode: string | null;
  clock: ServerClock;
  /** The current handshake QR while offering (replaced on every rotation). */
  offer: HandshakeTokenMessage | null;
  offering: boolean;
  replay: ReplayData | null;
  /** The last `handshakeRecorded` (local receive time). */
  recorded: { readonly handshakeId: string; readonly at: number } | null;
  /** Server-owned room facts: analytics consent and survey progress (not game state). */
  room: RoomMessage | null;
  /** Cards this client has played, acked by the server, by handshake id. */
  submitted: Readonly<Record<string, CardId>>;
  toasts: readonly Toast[];
}

const initial: GameState = {
  session: null,
  status: "idle",
  statusDetail: null,
  welcome: null,
  view: null,
  publicView: null,
  pairingCode: null,
  clock: { serverNow: 0, receivedAt: 0, scale: 1 },
  offer: null,
  offering: false,
  replay: null,
  recorded: null,
  room: null,
  submitted: {},
  toasts: [],
};

export const useGame = create<GameState>(() => ({ ...initial }));

let connection: GameConnection | null = null;
let toastId = 1;

export function toast(text: string, tone: Toast["tone"] = "info", ms = 4_000): void {
  const id = toastId++;
  // At most two at a time; the same text replaces itself.
  useGame.setState((s) => ({
    toasts: [...s.toasts.filter((t) => t.text !== text).slice(-1), { id, text, tone }],
  }));
  setTimeout(() => useGame.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), ms);
}

export function handleMessage(m: ServerMessage, receivedAt: number): void {
  switch (m.type) {
    case "welcome":
      useGame.setState((s) => ({
        welcome: m,
        pairingCode: m.roomCode === null ? s.pairingCode : null,
        clock: { ...s.clock, scale: m.timeScale ?? 1 },
      }));
      return;
    case "pairingCode":
      useGame.setState({ pairingCode: m.code });
      return;
    case "view": {
      const clock = (s: GameState): ServerClock => ({
        serverNow: m.serverNow,
        receivedAt,
        scale: s.clock.scale,
      });
      if (m.view.kind === "public") {
        const publicView = m.view;
        useGame.setState((s) => ({ publicView, pairingCode: null, clock: clock(s) }));
        return;
      }
      if (m.view.kind !== "player") return;
      const view = m.view;
      useGame.setState((s) => {
        const phase = view.public.phase;
        const mingling = phase.kind === "mingle" && !phase.closing;
        return {
          view,
          clock: clock(s),
          offering: mingling && view.me.alive ? s.offering : false,
          offer: mingling && view.me.alive ? s.offer : null,
        };
      });
      return;
    }
    case "handshakeToken":
      useGame.setState({ offer: m, offering: true });
      return;
    case "offerEnded":
      useGame.setState({ offer: null, offering: false });
      return;
    case "handshakeRecorded":
      useGame.setState({ recorded: { handshakeId: m.handshakeId, at: receivedAt } });
      return;
    case "replay":
      useGame.setState({ replay: m.replay });
      return;
    case "room":
      useGame.setState({ room: m });
      return;
    case "error":
      toast(m.error.message, "error");
      return;
    default:
      return;
  }
}

/** Connects the store to the server with a player session. */
export function connect(session: Session, factory?: SocketFactory): void {
  disconnect();
  useGame.setState({ ...initial, session, status: "connecting" });
  open({ kind: "player", token: session.token }, undefined, factory);
}

/**
 * Connects the store as a big screen (§15.8). `token` is the screen session kept from an earlier
 * visit, or null for a new one; `onToken` is told when the server hands out a new one.
 */
export function connectScreen(
  token: string | null,
  onToken: (token: string | null) => void,
  factory?: SocketFactory,
): void {
  disconnect();
  useGame.setState({ ...initial, status: "connecting" });
  open({ kind: "screen", token }, onToken, factory);
}

function open(
  identity: Identity,
  onToken: ((token: string | null) => void) | undefined,
  factory: SocketFactory | undefined,
): void {
  connection = new GameConnection(
    identity,
    {
      onStatus: (status, detail) => useGame.setState({ status, statusDetail: detail ?? null }),
      onMessage: handleMessage,
      onToken,
    },
    factory,
  );
  connection.start();
}

export function disconnect(): void {
  connection?.stop();
  connection = null;
}

/**
 * Sends a message. Failed acks show their (neutral, server-written) reason as a toast unless
 * `quiet`, and always resolve with the ack.
 */
export async function send(message: Outgoing, opts: { quiet?: boolean } = {}): Promise<Ack> {
  if (!connection) return { ok: false, error: { code: "disconnected", message: "Not connected." } };
  const ack = await connection.send(message);
  if (!ack.ok && !opts.quiet && ack.error) toast(ack.error.message, "error");
  return ack;
}

export async function playCard(handshakeId: string, card: CardId): Promise<Ack> {
  const ack = await send({ type: "playCard", handshakeId, card });
  if (ack.ok) {
    useGame.setState((s) => ({ submitted: { ...s.submitted, [handshakeId]: card } }));
  }
  return ack;
}

export async function offerHand(): Promise<void> {
  useGame.setState({ offering: true, offer: null });
  const ack = await send({ type: "offerHand" });
  if (!ack.ok) useGame.setState({ offering: false });
}

export async function cancelOffer(): Promise<void> {
  useGame.setState({ offering: false, offer: null });
  await send({ type: "cancelOffer" }, { quiet: true });
}

/** The server's clock at local time `at`, from the last measurement. */
export function serverTimeAt(clock: ServerClock, at: number): number {
  if (clock.receivedAt === 0) return at;
  return clock.serverNow + (at - clock.receivedAt) * clock.scale;
}

/** The server's clock now, as far as this client knows. */
export function serverNow(): number {
  return serverTimeAt(useGame.getState().clock, Date.now());
}

/** Test helper. */
export function resetStore(): void {
  disconnect();
  useGame.setState({ ...initial });
}
